import { Observable } from 'rxjs';
import {
  TpvOperatorResult,
  TpvOperatorListItem,
  TpvOperatorDetail,
  OperatorMerchants,
} from '@app/domain/models/tpv/tpv-operator.model';
import {
  CreateTpvOperatorRequest,
  UpdateTpvOperatorRequest,
} from '@app/data/dtos/tpv/tpv-operator.dto';

/**
 * Filtros del listado de operadores (`GET …/operators/`), aplicados SERVER-SIDE.
 * Ausente = "todos". El backend ya filtra; el front no debe filtrar en memoria.
 */
export interface OperatorListFilters {
  /** Búsqueda por nombre o email. */
  search?: string;
  /** Rol: `OPERADOR` / `GESTION` (alias de rol_id 3 / 2). */
  roleName?: string;
  /** Estado(s): `ACTIVO` / `INACTIVO`. */
  status?: string[];
  /** `true` = solo operadores company-wide (ven todos los comercios). */
  companyWide?: boolean;
}

/**
 * Port para el alta, listado y scoping de operadores TPV nativos
 * (`/api/v1/tpv/companies/<id_company>/operators/`, micro TPV).
 * Crea el usuario desde cero (User + cuenta sintética + gestor TPV) con scoping
 * opcional por comercio. Permiso backend: ManageTpvCompanyPermission.
 *
 * `idCompany` acepta el PK entero (org_id_company) o el UUID `id_company`
 * (empresas TPV-only): el back resuelve ambos.
 */
export abstract class TpvOperatorRepository {
  /**
   * Crea un operador en la Company indicada. `id_merchants` ausente/vacío =>
   * operador company-wide. `idempotencyKey` (uuid v4, UNA por intento de alta,
   * reusada en el reintento): si la 1ª petición se colgó pero el usuario se
   * creó, el reintento devuelve el 201 original en vez de 409 "ya existe".
   */
  abstract createOperator(
    idCompany: number | string,
    request: CreateTpvOperatorRequest,
    idempotencyKey?: string,
  ): Observable<TpvOperatorResult>;

  /**
   * Lista los operadores TPV de la Company (`GET …/operators/`, §4.B). Acepta filtros
   * server-side (búsqueda/rol/estado/company_wide); ausentes = todos.
   */
  abstract listOperators(
    idCompany: number | string,
    filters?: OperatorListFilters,
  ): Observable<TpvOperatorListItem[]>;

  /** Detalle de UN operador (`GET …/operators/<idUser>/`). Incluye first/last/phone. */
  abstract getOperator(
    idCompany: number | string,
    idUser: number | string,
  ): Observable<TpvOperatorDetail>;

  /** Edición parcial de un operador (`PATCH …/operators/<idUser>/`). */
  abstract updateOperator(
    idCompany: number | string,
    idUser: number | string,
    request: UpdateTpvOperatorRequest,
  ): Observable<TpvOperatorDetail>;

  /** Baja del operador (`DELETE …/operators/<idUser>/`): desactiva el gestor (reversible). */
  abstract deleteOperator(
    idCompany: number | string,
    idUser: number | string,
  ): Observable<void>;

  /** Allow-list actual de un operador (`GET …/operators/<idUser>/merchants/`, §4.C). */
  abstract getOperatorMerchants(
    idCompany: number | string,
    idUser: number | string,
  ): Observable<OperatorMerchants>;

  /**
   * Reemplaza (PUT) la allow-list por exactamente `idMerchants`.
   * Lista vacía ⇒ el operador queda company-wide (footgun, ver §4.C).
   */
  abstract replaceOperatorMerchants(
    idCompany: number | string,
    idUser: number | string,
    idMerchants: string[],
  ): Observable<OperatorMerchants>;

  /** Agrega (POST, unión) `idMerchants` a la allow-list actual. */
  abstract addOperatorMerchants(
    idCompany: number | string,
    idUser: number | string,
    idMerchants: string[],
  ): Observable<OperatorMerchants>;

  /** Quita UN comercio (DELETE) de la allow-list. */
  abstract removeOperatorMerchant(
    idCompany: number | string,
    idUser: number | string,
    idMerchant: string,
  ): Observable<OperatorMerchants>;
}
